import React, { useState } from 'react';
import { Container, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { EditableLineItem, LineItem } from '@payer-admin-ui/api/src/orders/models/LineItem';
import { ItemType } from '@payer-admin-ui/api/src/invoices/models/ItemType';
import { StandardButton, StandardButtonType } from '@payer-admin-ui/components-v2/src/buttons/StandardButton';
import {
  fromCents,
  fromCentsOrUndefined,
  toCents,
  toCentsOrUndefined,
  toNumber,
  toNumberOrNull,
} from '@payer-admin-ui/components-v2/src/functions/numbers';
import { LineItemInputs } from '../../invoices/LineItemInputs';

const mapEditableLineItemToLineItem = (item: EditableLineItem): Omit<LineItem, 'position'> | undefined =>
  item.subtotalPrice &&
  item.subtotalVatAmount &&
  item.vatPercentage &&
  item.quantity &&
  item.articleNumber &&
  item.description
    ? {
        ...item,
        calculationMode: item.calculationMode ?? 'INC_VAT_ROUNDING_PER_ROW',
        vatAccountCode: toNumberOrNull(item.vatAccountCode) ?? undefined,
        salesAccountCode: toNumberOrNull(item.salesAccountCode) ?? undefined,
        description: item.description,
        articleNumber: item.articleNumber,
        subtotalPrice: toCents(item.subtotalPrice),
        subtotalVatAmount: toCents(item.subtotalVatAmount),
        vatPercentage: toCents(item.vatPercentage),
        unitPrice: toCentsOrUndefined(item.unitPrice),
        unitVatAmount: toCentsOrUndefined(item.unitVatAmount),
        quantity: toNumber(item.quantity),
        itemType: ItemType.FREEFORM,
      }
    : undefined;

const mapLineItemToEditableLineItem = (item: LineItem): EditableLineItem => ({
  ...item,
  quantity: item.quantity.toString(),
  unitPrice: fromCentsOrUndefined(item.unitPrice)?.toString(),
  unitVatAmount: fromCentsOrUndefined(item.unitVatAmount)?.toString(),
  vatPercentage: fromCents(item.vatPercentage).toString(),
  subtotalPrice: fromCents(item.subtotalPrice).toString(),
  subtotalVatAmount: fromCents(item.subtotalVatAmount).toString(),
  salesAccountCode: item.salesAccountCode?.toString(),
  vatAccountCode: item.vatAccountCode?.toString(),
});

export type EditItemRowModalProps = {
  currencyCode: CurrencyCode;
  item?: LineItem;
  onClose: () => void;
  onAddOrUpdateItemRow: (itemToAdd: Omit<LineItem, 'position'>) => void;
};
const EditItemRowModal = ({
  item: originalItem,
  currencyCode,
  onAddOrUpdateItemRow,
  onClose,
}: EditItemRowModalProps) => {
  const { t } = useTranslation();
  const [item, setItem] = useState<EditableLineItem>(originalItem ? mapLineItemToEditableLineItem(originalItem) : {});

  const patchItem = (itemPatch: EditableLineItem) => setItem({ ...item, ...itemPatch });
  return (
    <Modal size="lg" isOpen={true} toggle={onClose}>
      <ModalHeader toggle={onClose}>{t('invoices.CreateInvoicePage.itemRowModal')}</ModalHeader>
      <ModalBody>
        <Container>
          <Row>
            <LineItemInputs item={item} patchItem={patchItem} currencyCode={currencyCode} />
          </Row>
        </Container>
      </ModalBody>
      <ModalFooter>
        <StandardButton type={StandardButtonType.CANCEL} onClick={onClose} />
        <StandardButton
          useIcon
          type={StandardButtonType.OK}
          onClick={() => {
            const lineItem = mapEditableLineItemToLineItem(item);
            if (lineItem) {
              onAddOrUpdateItemRow(lineItem);
            }
          }}
        />
      </ModalFooter>
    </Modal>
  );
};

export { EditItemRowModal };
